import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { translations } from "./translations";

const LangContext = createContext(null);

const STORAGE_KEY = "mu-lang";

function interpolate(str, vars) {
  if (!vars) return str;
  return String(str).replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : `{${key}}`
  );
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === "he" || saved === "en" ? saved : "en";
    } catch {
      return "en";
    }
  });

  const setLang = useCallback((next) => {
    setLangState(next === "he" ? "he" : "en");
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = lang === "he" ? "he" : "en";
    // Keep LTR layout (decorative nav/images). Only translate strings.
    document.documentElement.dir = "ltr";
    document.body.classList.toggle("mu-lang-he", lang === "he");
  }, [lang]);

  const t = useCallback(
    (key, vars) => {
      const dict = translations[lang] || translations.en;
      const fallback = translations.en[key];
      const value = dict[key] ?? fallback ?? key;
      return interpolate(value, vars);
    },
    [lang]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
      isHe: lang === "he",
    }),
    [lang, setLang, t]
  );

  return (
    <LangContext.Provider value={value}>{children}</LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) {
    throw new Error("useLang must be used within LangProvider");
  }
  return ctx;
}
